import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  LayoutDashboard, FileText, MessageSquare, Users, 
  Plus, Edit, Trash2, Eye, EyeOff, LogIn, LogOut 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { value: 'erp', label: 'ERP Implementation' },
  { value: 'cloud', label: 'Cloud Migration' },
  { value: 'custom', label: 'Custom Applications' },
  { value: 'managed', label: 'Managed Services' }
];

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [activeTab, setActiveTab] = useState('dashboard');
  const [caseStudies, setCaseStudies] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Form states
  const [caseStudyForm, setCaseStudyForm] = useState({
    title: '', client: '', industry: '', challenge: '', 
    solution: '', results: '', category: '', image_url: ''
  });
  const [testimonialForm, setTestimonialForm] = useState({
    name: '', title: '', company: '', quote: '', rating: 5, image_url: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      setIsAuthenticated(true);
      fetchAllData();
    }
  }, []);

  const fetchAllData = async () => {
    try {
      const [csRes, testRes, leadsRes] = await Promise.all([
        axios.get(`${API}/case-studies`),
        axios.get(`${API}/testimonials?active_only=false`),
        axios.get(`${API}/leads`)
      ]);
      setCaseStudies(csRes.data);
      setTestimonials(testRes.data);
      setLeads(leadsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      localStorage.setItem('admin_token', response.data.token);
      setIsAuthenticated(true);
      fetchAllData();
      toast.success('Logged in successfully');
    } catch (error) {
      toast.error('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    toast.success('Logged out successfully');
  };

  // Case Study CRUD
  const handleCaseStudySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingId) {
        await axios.put(`${API}/case-studies/${editingId}`, caseStudyForm);
        toast.success('Case study updated');
      } else {
        await axios.post(`${API}/case-studies`, caseStudyForm);
        toast.success('Case study created');
      }
      fetchAllData();
      setDialogOpen(false);
      resetCaseStudyForm();
    } catch (error) {
      toast.error('Failed to save case study');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCaseStudy = (cs) => {
    setCaseStudyForm({
      title: cs.title,
      client: cs.client,
      industry: cs.industry,
      challenge: cs.challenge,
      solution: cs.solution,
      results: cs.results,
      category: cs.category,
      image_url: cs.image_url || ''
    });
    setEditingId(cs.id);
    setDialogOpen(true);
  };

  const handleDeleteCaseStudy = async (id) => {
    if (!window.confirm('Are you sure you want to delete this case study?')) return;
    try {
      await axios.delete(`${API}/case-studies/${id}`);
      toast.success('Case study deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete case study');
    }
  };

  const resetCaseStudyForm = () => {
    setCaseStudyForm({
      title: '', client: '', industry: '', challenge: '', 
      solution: '', results: '', category: '', image_url: ''
    });
    setEditingId(null);
  };

  // Testimonial CRUD
  const handleTestimonialSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingId) {
        await axios.put(`${API}/testimonials/${editingId}`, testimonialForm);
        toast.success('Testimonial updated');
      } else {
        await axios.post(`${API}/testimonials`, testimonialForm);
        toast.success('Testimonial created');
      }
      fetchAllData();
      setDialogOpen(false);
      resetTestimonialForm();
    } catch (error) {
      toast.error('Failed to save testimonial');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTestimonial = (t) => {
    setTestimonialForm({
      name: t.name,
      title: t.title,
      company: t.company,
      quote: t.quote,
      rating: t.rating,
      image_url: t.image_url || ''
    });
    setEditingId(t.id);
    setDialogOpen(true);
  };

  const handleDeleteTestimonial = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    try {
      await axios.delete(`${API}/testimonials/${id}`);
      toast.success('Testimonial deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
  };

  const handleToggleTestimonial = async (id) => {
    try {
      await axios.patch(`${API}/testimonials/${id}/toggle`);
      toast.success('Testimonial visibility toggled');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to toggle testimonial');
    }
  };

  const resetTestimonialForm = () => {
    setTestimonialForm({
      name: '', title: '', company: '', quote: '', rating: 5, image_url: ''
    });
    setEditingId(null);
  };

  // Lead management
  const handleDeleteLead = async (id) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await axios.delete(`${API}/leads/${id}`);
      toast.success('Lead deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  // Reset website content (preserves leads)
  const handleReseed = async () => {
    if (!window.confirm('⚠️ WARNING: This will DELETE and reset:\n\n• All Case Studies\n• All Testimonials\n\nYour leads/inquiries will be preserved.\n\nAre you sure?')) return;
    setLoading(true);
    try {
      await axios.post(`${API}/reseed`);
      toast.success('Website content reset successfully! Leads preserved.');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to reset website content');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="pt-20 min-h-screen section-dark flex items-center justify-center" data-testid="admin-login-page">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="font-heading text-2xl text-center">Admin Login</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username" className="font-body">Username</Label>
                <Input
                  id="username"
                  value={credentials.username}
                  onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                  required
                  className="font-body"
                  data-testid="admin-username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="font-body">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                  required
                  className="font-body"
                  data-testid="admin-password"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-oracle-red hover:bg-oracle-red-dark font-body"
                disabled={loading}
                data-testid="admin-login-btn"
              >
                <LogIn className="w-4 h-4 mr-2" />
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Admin Header */}
      <div className="bg-deep-obsidian border-b border-slate-800 py-4">
        <div className="container-custom flex justify-between items-center">
          <h1 className="font-heading text-xl text-white">Admin Dashboard</h1>
          <Button 
            variant="outline" 
            className="border-slate-600 text-white hover:bg-slate-800 font-body"
            onClick={handleLogout}
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Admin Content */}
      <div className="container-custom py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8">
            <TabsTrigger value="dashboard" className="font-body">
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="case-studies" className="font-body" data-testid="admin-tab-case-studies">
              <FileText className="w-4 h-4 mr-2" />
              Case Studies
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="font-body" data-testid="admin-tab-testimonials">
              <MessageSquare className="w-4 h-4 mr-2" />
              Testimonials
            </TabsTrigger>
            <TabsTrigger value="leads" className="font-body" data-testid="admin-tab-leads">
              <Users className="w-4 h-4 mr-2" />
              Leads
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <div className="grid md:grid-cols-3 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-body text-slate-600">Case Studies</CardTitle>
                  <FileText className="w-5 h-5 text-oracle-red" />
                </CardHeader>
                <CardContent>
                  <div className="font-heading text-3xl font-bold">{caseStudies.length}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-body text-slate-600">Testimonials</CardTitle>
                  <MessageSquare className="w-5 h-5 text-oracle-red" />
                </CardHeader>
                <CardContent>
                  <div className="font-heading text-3xl font-bold">{testimonials.length}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-body text-slate-600">Leads</CardTitle>
                  <Users className="w-5 h-5 text-oracle-red" />
                </CardHeader>
                <CardContent>
                  <div className="font-heading text-3xl font-bold">{leads.length}</div>
                </CardContent>
              </Card>
            </div>
            
            {/* Reseed Data Section */}
            <Card className="mt-8 border-orange-200 bg-orange-50">
              <CardHeader>
                <CardTitle className="text-sm font-body text-orange-800 flex items-center">
                  <LayoutDashboard className="w-5 h-5 mr-2" />
                  Data Management
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-orange-700 mb-4 font-body">
                  Reset case studies and testimonials to default values. Your leads/inquiries will be preserved.
                </p>
                <Button 
                  onClick={handleReseed}
                  disabled={loading}
                  className="bg-orange-600 hover:bg-orange-700 text-white font-body"
                  data-testid="reseed-btn"
                >
                  {loading ? 'Resetting...' : 'Reset Website Content'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Case Studies Tab */}
          <TabsContent value="case-studies">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-heading text-2xl font-bold">Case Studies</h2>
              <Dialog open={dialogOpen && activeTab === 'case-studies'} onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) resetCaseStudyForm();
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-oracle-red hover:bg-oracle-red-dark font-body" data-testid="add-case-study-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Case Study
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading">
                      {editingId ? 'Edit Case Study' : 'Add Case Study'}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCaseStudySubmit} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="font-body">Title *</Label>
                        <Input
                          value={caseStudyForm.title}
                          onChange={(e) => setCaseStudyForm({...caseStudyForm, title: e.target.value})}
                          required
                          className="font-body"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-body">Client *</Label>
                        <Input
                          value={caseStudyForm.client}
                          onChange={(e) => setCaseStudyForm({...caseStudyForm, client: e.target.value})}
                          required
                          className="font-body"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="font-body">Industry *</Label>
                        <Input
                          value={caseStudyForm.industry}
                          onChange={(e) => setCaseStudyForm({...caseStudyForm, industry: e.target.value})}
                          required
                          className="font-body"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-body">Category *</Label>
                        <Select 
                          value={caseStudyForm.category}
                          onValueChange={(value) => setCaseStudyForm({...caseStudyForm, category: value})}
                        >
                          <SelectTrigger className="font-body">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat.value} value={cat.value}>
                                {cat.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="font-body">Challenge *</Label>
                      <Textarea
                        value={caseStudyForm.challenge}
                        onChange={(e) => setCaseStudyForm({...caseStudyForm, challenge: e.target.value})}
                        required
                        rows={3}
                        className="font-body"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-body">Solution *</Label>
                      <Textarea
                        value={caseStudyForm.solution}
                        onChange={(e) => setCaseStudyForm({...caseStudyForm, solution: e.target.value})}
                        required
                        rows={3}
                        className="font-body"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-body">Results *</Label>
                      <Textarea
                        value={caseStudyForm.results}
                        onChange={(e) => setCaseStudyForm({...caseStudyForm, results: e.target.value})}
                        required
                        rows={3}
                        className="font-body"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-body">Image URL</Label>
                      <Input
                        value={caseStudyForm.image_url}
                        onChange={(e) => setCaseStudyForm({...caseStudyForm, image_url: e.target.value})}
                        placeholder="https://..."
                        className="font-body"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-oracle-red hover:bg-oracle-red-dark font-body"
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : (editingId ? 'Update Case Study' : 'Create Case Study')}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-body">Title</TableHead>
                    <TableHead className="font-body">Client</TableHead>
                    <TableHead className="font-body">Category</TableHead>
                    <TableHead className="font-body">Industry</TableHead>
                    <TableHead className="font-body text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {caseStudies.map((cs) => (
                    <TableRow key={cs.id}>
                      <TableCell className="font-body font-medium">{cs.title}</TableCell>
                      <TableCell className="font-body">{cs.client}</TableCell>
                      <TableCell className="font-body capitalize">{cs.category}</TableCell>
                      <TableCell className="font-body">{cs.industry}</TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleEditCaseStudy(cs)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteCaseStudy(cs.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Testimonials Tab */}
          <TabsContent value="testimonials">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-heading text-2xl font-bold">Testimonials</h2>
              <Dialog open={dialogOpen && activeTab === 'testimonials'} onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) resetTestimonialForm();
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-oracle-red hover:bg-oracle-red-dark font-body" data-testid="add-testimonial-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Testimonial
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-heading">
                      {editingId ? 'Edit Testimonial' : 'Add Testimonial'}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleTestimonialSubmit} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="font-body">Name *</Label>
                        <Input
                          value={testimonialForm.name}
                          onChange={(e) => setTestimonialForm({...testimonialForm, name: e.target.value})}
                          required
                          className="font-body"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-body">Title *</Label>
                        <Input
                          value={testimonialForm.title}
                          onChange={(e) => setTestimonialForm({...testimonialForm, title: e.target.value})}
                          required
                          className="font-body"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="font-body">Company *</Label>
                        <Input
                          value={testimonialForm.company}
                          onChange={(e) => setTestimonialForm({...testimonialForm, company: e.target.value})}
                          required
                          className="font-body"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-body">Rating</Label>
                        <Select 
                          value={String(testimonialForm.rating)}
                          onValueChange={(value) => setTestimonialForm({...testimonialForm, rating: parseInt(value)})}
                        >
                          <SelectTrigger className="font-body">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[5, 4, 3, 2, 1].map((r) => (
                              <SelectItem key={r} value={String(r)}>
                                {r} Stars
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="font-body">Quote *</Label>
                      <Textarea
                        value={testimonialForm.quote}
                        onChange={(e) => setTestimonialForm({...testimonialForm, quote: e.target.value})}
                        required
                        rows={4}
                        className="font-body"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-body">Image URL</Label>
                      <Input
                        value={testimonialForm.image_url}
                        onChange={(e) => setTestimonialForm({...testimonialForm, image_url: e.target.value})}
                        placeholder="https://..."
                        className="font-body"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-oracle-red hover:bg-oracle-red-dark font-body"
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : (editingId ? 'Update Testimonial' : 'Create Testimonial')}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-body">Name</TableHead>
                    <TableHead className="font-body">Title</TableHead>
                    <TableHead className="font-body">Company</TableHead>
                    <TableHead className="font-body">Rating</TableHead>
                    <TableHead className="font-body">Status</TableHead>
                    <TableHead className="font-body text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {testimonials.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell className="font-body font-medium">{t.name}</TableCell>
                      <TableCell className="font-body">{t.title}</TableCell>
                      <TableCell className="font-body">{t.company}</TableCell>
                      <TableCell className="font-body">{t.rating}/5</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-body ${
                          t.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'
                        }`}>
                          {t.is_active ? 'Active' : 'Hidden'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleToggleTestimonial(t.id)}
                        >
                          {t.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleEditTestimonial(t)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteTestimonial(t.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Leads Tab */}
          <TabsContent value="leads">
            <h2 className="font-heading text-2xl font-bold mb-6">Leads</h2>
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-body">Name</TableHead>
                    <TableHead className="font-body">Email</TableHead>
                    <TableHead className="font-body">Company</TableHead>
                    <TableHead className="font-body">Type</TableHead>
                    <TableHead className="font-body">Date</TableHead>
                    <TableHead className="font-body text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((lead) => (
                    <TableRow key={lead.id}>
                      <TableCell className="font-body font-medium">{lead.name}</TableCell>
                      <TableCell className="font-body">{lead.email}</TableCell>
                      <TableCell className="font-body">{lead.company}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-body capitalize ${
                          lead.lead_type === 'consultation' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-purple-100 text-purple-700'
                        }`}>
                          {lead.lead_type}
                        </span>
                      </TableCell>
                      <TableCell className="font-body">
                        {new Date(lead.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteLead(lead.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
